import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  ColumnDef,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { DataTable, DataTableHandle } from '@/components/ui/DataTable';
import {
  ColumnManagerPopover,
  ManagedColumn,
} from '@/components/ui/ColumnManagerPopover';
import { DateRangeFilter } from '@/components/ui/DateRangeFilter';
import { ExportCsvButton } from '@/components/ui/ExportCsvButton';
import { StatusFilterSelect } from '@/components/ui/StatusFilterSelect';
import { TableStateGate } from '@/components/ui/TableStateGate';
import { StudySiteData } from '@/types/study';
import { useSitesList } from '@/hooks/sites/useSitesList';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { useDateRangeColumnFilters } from '@/hooks/useDateRangeColumnFilters';
import { useDebounce } from '@/hooks/useDebounce';
import { useUser } from '@/providers/UserProvider';
import { getEntityStatusInfo } from '@/utils/entityStatuses';
import {
  buildSiteColumns,
  siteColumnLabels,
} from '@/pages/Sites/siteTableColumns';

interface ScopedSitesTableProps {
  studyId?: string;
  variant?: string;
  className?: string;
  /** Renders the table with no toolbar (search/filters/export/columns), for
   * callers that embed it as a plain read-only list. Mirrors
   * `ScopedSubmissionsTable`'s `hideSearch`. */
  hideToolbar?: boolean;
  /** Rows per page. Omit to render every row unpaginated (the default).
   * Pagination here is purely client-side — the fetch below deliberately
   * sends no page/perPage, so `data` is the whole study-scoped set and the
   * country/date-range filters and CSV export still see every row. */
  pageSize?: number;
}

// Only one sites table is ever on screen at a time, and its column set is the
// same on every study — so the persisted layout is keyed by table, NOT by
// study. (`ScopedSubmissionsTable` folds its owner id into the key because two
// same-scope instances share the Submissions tab and would otherwise overwrite
// each other; that doesn't apply here, and a per-study key would just force the
// user to re-hide the same column on every study they open.)
const SITES_TABLE_ID = 'study-sites';

// Preserves the column order this tab has always shown, with the two new date
// columns appended — so an existing user's view isn't reshuffled under them.
const DEFAULT_COLUMN_ORDER = [
  'site_name',
  'display_id',
  'status',
  'country',
  'pi_name',
  'submitted_at',
  'expiration_date',
];

const MANAGEABLE_COLUMNS = [
  { id: 'site_name', defaultVisible: true, defaultPinned: 'left' as const },
  { id: 'display_id', defaultVisible: true },
  { id: 'status', defaultVisible: true },
  { id: 'country', defaultVisible: true },
  { id: 'pi_name', defaultVisible: true },
  { id: 'submitted_at', defaultVisible: true },
  { id: 'expiration_date', defaultVisible: true },
];

// Columns with a DateRangeFilter — kept in one place so the toolbar and the
// useDateRangeColumnFilters wiring below can't drift apart.
const DATE_RANGE_FILTER_COLUMNS = ['submitted_at', 'expiration_date'];

export default function ScopedSitesTable({
  studyId,
  variant,
  className = 'bg-white rounded-lg border border-gray-200',
  hideToolbar = false,
  pageSize,
}: ScopedSitesTableProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { userData } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [statusFilter, setStatusFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const dataTableRef = useRef<DataTableHandle>(null);
  const [exportRowCount, setExportRowCount] = useState(0);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize ?? 0,
  });
  // Sort is held here rather than inside DataTable: the TableStateGate below
  // unmounts the table whenever a fetch is in flight or the filters exclude
  // every row, and DataTable's own sort state would go with it — so picking a
  // status (or filtering down to nothing and back) silently dropped the sort
  // the user had applied.
  const [sorting, setSorting] = useState<SortingState>([]);

  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
    resetToDefaults,
  } = useColumnPreferences({
    tableId: SITES_TABLE_ID,
    userId: userData?.id,
    columns: MANAGEABLE_COLUMNS,
    defaultOrder: DEFAULT_COLUMN_ORDER,
  });

  const { columnFilters, setColumnFilters, dateRangeValue, setDateRangeValue } =
    useDateRangeColumnFilters({
      dateRangeColumns: DATE_RANGE_FILTER_COLUMNS,
      searchParams,
    });

  const handleRowClick = useCallback(
    (site: StudySiteData, event: React.MouseEvent | React.KeyboardEvent) => {
      // Mouse only: let right-click and middle-click behave normally. A
      // keyboard activation has no `button`, so it must not be filtered here.
      if ('button' in event && event.button !== 0) return;

      const url = site.show_path;

      if (event.ctrlKey || event.metaKey) {
        window.open(url, '_blank');
        return;
      }

      navigate(url);
    },
    [navigate]
  );

  // Search and status are narrowed server-side (the endpoint scopes them to
  // this study); country and the two date ranges are applied in the browser,
  // exactly as on the Sites index. No page/perPage — pagination is out of
  // scope for this tab, so this is the full set, which is also what makes the
  // client-side filtering above and DataTable's own CSV export correct.
  const {
    data: allData = [],
    isLoading,
    error,
    countries: countryOptions = [],
  } = useSitesList({
    studyId,
    // Spread in only when set. `search`/`status` are part of the React Query
    // key, so passing them as empty strings would build a key that misses the
    // one Studies/Show.tsx has already filled with this exact request —
    // costing a second identical /sites round trip, and a spinner in place of
    // rows that were in hand, every time the Sites tab is opened.
    ...(debouncedSearchQuery ? { search: debouncedSearchQuery } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    order: 'desc',
    sort: 'created_at',
  });

  const data = useMemo(
    () =>
      countryFilter
        ? allData.filter(site => site.countries?.includes(countryFilter))
        : allData,
    [allData, countryFilter]
  );

  // True whenever the rows on screen are a narrowed view of the study's sites
  // — server-side (search/status) or in the browser (country, date ranges).
  // Drives both the "nothing matched" copy and the export count below.
  const hasActiveFilters =
    Boolean(debouncedSearchQuery) ||
    Boolean(statusFilter) ||
    Boolean(countryFilter) ||
    columnFilters.length > 0;

  // "No sites yet." is only true of a study with no sites at all — saying it
  // to someone whose search just missed reads as "this study is empty", which
  // it isn't.
  const emptyMessage = hasActiveFilters
    ? t('sites.empty')
    : t('sites.empty_yet');

  // DataTable reports the exportable (post-filter) row count, but the gate
  // below unmounts it the moment `data` is empty — so it never gets to report
  // the 0, and the last non-zero count would leave "Export CSV" enabled on an
  // empty table, where clicking it silently downloads nothing.
  const exportableRowCount = data.length === 0 ? 0 : exportRowCount;

  // Any change that narrows the row set has to send the reader back to page
  // one — otherwise a filter applied while on page 3 leaves them staring at a
  // table that now has only one page of results.
  useEffect(() => {
    setPagination(prev =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
  }, [debouncedSearchQuery, statusFilter, countryFilter, columnFilters]);

  const columns = useMemo(() => {
    const siteColumns = buildSiteColumns(t);
    return [
      siteColumns.site_name,
      siteColumns.display_id,
      siteColumns.status,
      siteColumns.country,
      siteColumns.pi_name,
      siteColumns.submitted_at,
      siteColumns.expiration_date,
    ] as ColumnDef<StudySiteData, unknown>[];
  }, [t]);

  const managedColumns: ManagedColumn[] = useMemo(() => {
    const labels = siteColumnLabels(t);
    return MANAGEABLE_COLUMNS.map(({ id }) => ({ id, label: labels[id] }));
  }, [t]);

  // Unreachable in practice: Studies/Show.tsx only offers the Sites tab when
  // the study is NOT a Single Site Protocol. Left in place rather than deleted
  // — removing it is a separate question from this tab's table enhancements.
  // The `!isLoading` guard replaces the early-return spinner this component
  // used to have before the toolbar needed to stay mounted.
  const isSingleSiteCard =
    !isLoading && variant === 'Single Site Protocol' && data.length === 1;

  if (isSingleSiteCard) {
    const site = data[0];
    const statusInfo = site.status ? getEntityStatusInfo(site.status) : null;
    const submittedDate = site.submitted_at
      ? new Date(site.submitted_at).toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: '2-digit',
        })
      : '-';
    const expirationDate = site.expiration_date
      ? new Date(site.expiration_date).toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: '2-digit',
        })
      : '-';

    return (
      <div
        className="bg-white rounded-lg border border-gray-200 p-8 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => navigate(site.show_path)}
      >
        <div className="grid grid-cols-2 gap-y-6 gap-x-16">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Site</p>
            <p className="text-gray-900 font-medium">{site.site_name || '-'}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">
              Submission ID
            </p>
            <p className="text-gray-900">{site.display_id || '-'}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Status</p>
            {statusInfo ? (
              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: statusInfo.color }}
              >
                {statusInfo.label}
              </span>
            ) : (
              <p className="text-gray-900">-</p>
            )}
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Submitted</p>
            <p className="text-gray-900">{submittedDate}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Expiration</p>
            <p className="text-gray-900">{expirationDate}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Country</p>
            <p className="text-gray-900">
              {site.countries?.length > 0 ? site.countries[0] : '-'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">PI</p>
            <p className="text-gray-900">{site.pi_name || '-'}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">
              Assigned AM
            </p>
            <p className="text-gray-900">-</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {!hideToolbar && (
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder={t('sites.search_placeholder')}
                aria-label={t('sites.search_placeholder')}
                className="pl-10 w-80"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <StatusFilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              allStatusesLabel={t('sites.filter.all_statuses')}
              ariaLabel={t('sites.fields.status')}
            />
            <Select
              value={countryFilter || 'all'}
              onValueChange={v => setCountryFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger
                aria-label={t('sites.filter.country_label')}
                className="px-4 py-2 border border-[#e9eaeb] rounded-md text-sm text-gray-600 font-medium hover:bg-gray-50 bg-white h-auto w-auto gap-2 [&>svg]:opacity-100"
              >
                <span>{countryFilter || t('sites.filter.country_label')}</span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('sites.filter.all_countries')}
                </SelectItem>
                {countryOptions.map(country => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateRangeFilter
              label={t('sites.fields.approved')}
              value={dateRangeValue('submitted_at')}
              onChange={setDateRangeValue('submitted_at')}
            />
            <DateRangeFilter
              label={t('sites.fields.expiration')}
              value={dateRangeValue('expiration_date')}
              onChange={setDateRangeValue('expiration_date')}
            />
            <div className="ml-auto flex items-center gap-2">
              <ExportCsvButton
                dataTableRef={dataTableRef}
                filename="study-sites.csv"
                rowCount={exportableRowCount}
              />
              <ColumnManagerPopover
                columns={managedColumns}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={setColumnVisibility}
                columnOrder={columnOrder}
                onColumnOrderChange={setColumnOrder}
                columnPinning={columnPinning}
                onColumnPinningChange={setColumnPinning}
                onReset={resetToDefaults}
              />
            </div>
          </div>
        </div>
      )}

      <TableStateGate
        isLoading={isLoading}
        error={error}
        isEmpty={data.length === 0}
        errorMessage={
          (error as Error)?.message || t('sites.errors.failed_to_load')
        }
        emptyMessage={emptyMessage}
      >
        <DataTable
          ref={dataTableRef}
          columns={columns}
          data={data}
          onRowClick={handleRowClick}
          emptyMessage={emptyMessage}
          sorting={sorting}
          onSortingChange={setSorting}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          columnOrder={columnOrder}
          onColumnOrderChange={setColumnOrder}
          columnPinning={columnPinning}
          onColumnPinningChange={setColumnPinning}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          {...(pageSize
            ? {
                pagination: {
                  pageIndex: pagination.pageIndex,
                  pageSize: pagination.pageSize,
                  onPaginationChange: setPagination,
                },
              }
            : {})}
          onExportRowCountChange={setExportRowCount}
        />
      </TableStateGate>
    </div>
  );
}
